import i18n from "src/i18n/config";
import { z } from "zod";

import { SimplePolicyRule } from "../types";

const stayOnSetting = { command: "changeSetting", section: "global", type: "int", key: "stay_on_while_plugged_in" };
// Android stores the power sources as a bit mask: AC = 1, USB = 2, wireless = 4.
const powerSources = ["1", "2", "4"] as const;
const allSourcesMask = powerSources.reduce((mask, source) => mask | Number(source), 0);
const stayAwakeSchema = z.object({ sources: z.array(z.enum(powerSources)) });

function fromMask(value: unknown) {
    const mask = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : NaN;
    if (Number.isNaN(mask) || (mask & ~allSourcesMask) !== 0) {
        return null;
    }
    return { sources: powerSources.filter((source) => (mask & Number(source)) !== 0) };
}

export const stayAwakeWhileCharging: SimplePolicyRule<z.infer<typeof stayAwakeSchema>> = {
    id: "stayAwakeWhileCharging",
    title: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.title"),
    description: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.description"),
    details: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.details"),
    summary: (display, { sources }) =>
        sources.length
            ? i18n.t("modals:addRule.rules.stayAwakeWhileCharging.summary", display)
            : i18n.t("modals:addRule.rules.stayAwakeWhileCharging.summaryNever"),
    categories: ["display", "power"],
    fields: [
        {
            type: "select",
            name: "sources",
            label: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.sources"),
            multiple: true,
            allSelectedLabel: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.allSources"),
            options: [
                { value: "1", label: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.options.ac") },
                { value: "2", label: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.options.usb") },
                { value: "4", label: () => i18n.t("modals:addRule.rules.stayAwakeWhileCharging.options.wireless") },
            ],
        },
    ],
    defaultValues: { sources: [...powerSources] },
    schema: stayAwakeSchema,
    command: stayOnSetting,
    toValues: ({ sources }) => String(sources.reduce((mask, source) => mask | Number(source), 0)),
    fromValues: fromMask,
    flags: [],
};
